import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import SearchableSelect from '../../components/SearchableSelect';
import { useSnackbar } from 'notistack';
import tipoFormularioService, { TipoFormulario } from '../../services/tipoFormularioService';

const defaultForm: TipoFormulario = {
  nroTipoFormulario: '',
  descripcion: '',
  area: 'STOCK',
  utilizaLibroIva: 'No',
  sumaResta: 'Suma',
  stockNegativo: 0,
  infoCons: 0,
  descuentaStock: 0,
  nota: ''
};

const areas = ['STOCK', 'BANCO', 'PRODUCCION', 'EXPEDICION', 'CONTABILID'];

const TipoFormularioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroTipoFormulario } = useParams();
  const [formData, setFormData] = useState<TipoFormulario>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTipoFormulario = useCallback(async () => {
    if (!nroTipoFormulario || nroTipoFormulario === 'nuevo') return;
    try {
      setLoading(true);
      const tipo = await tipoFormularioService.getTipoFormulario(nroTipoFormulario);
      const normalized = {
        ...tipo,
        utilizaLibroIva: tipo.utilizaLibroIva?.trim() === 'S' ? 'Si' : tipo.utilizaLibroIva?.trim() === 'N' ? 'No' : (tipo.utilizaLibroIva || 'No'),
        sumaResta: tipo.sumaResta?.trim() === 'S' ? 'Suma' : tipo.sumaResta?.trim() === 'R' ? 'Resta' : (tipo.sumaResta || 'Suma')
      };
      setFormData(normalized);
    } catch (error) {
      enqueueSnackbar('Error al cargar el tipo de formulario', { variant: 'error' });
      navigate('/sistema/tipo-formulario');
    } finally {
      setLoading(false);
    }
  }, [nroTipoFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroTipoFormulario && nroTipoFormulario !== 'nuevo') loadTipoFormulario();
  }, [nroTipoFormulario, loadTipoFormulario]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'stockNegativo' || name === 'descuentaStock') {
      setFormData(prev => ({ ...prev, [name]: Number(value) || 0 }));
    } else if (name === 'infoCons') {
      setFormData(prev => ({ ...prev, infoCons: value === '' ? undefined : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nroTipoFormulario.trim() || !formData.descripcion.trim()) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      if (nroTipoFormulario && nroTipoFormulario !== 'nuevo') {
        const payload = { ...formData, nroTipoFormulario: nroTipoFormulario.trim() };
        await tipoFormularioService.updateTipoFormulario(nroTipoFormulario.trim(), payload);
      } else {
        await tipoFormularioService.createTipoFormulario(formData);
      }

      enqueueSnackbar('Tipo de formulario guardado exitosamente', { variant: 'success' });
      navigate('/sistema/tipo-formulario');
    } catch (error: any) {
      console.error('Error al guardar tipo de formulario:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el tipo de formulario', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/tipo-formulario')}
              sx={{ textTransform: 'none' }}
            >
              Tipos de Formulario
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {nroTipoFormulario && nroTipoFormulario !== 'nuevo' ? 'Editar Tipo de Formulario' : 'Nuevo Tipo de Formulario'}
            </Typography>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                  <TextField 
                    name="nroTipoFormulario" 
                    label="N° Tipo" 
                    value={formData.nroTipoFormulario} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(nroTipoFormulario && nroTipoFormulario !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField 
                  name="descripcion" 
                  label="Descripción" 
                  value={formData.descripcion} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect
                  label="Área"
                  value={formData.area || null}
                  onChange={(val) => setFormData((prev) => ({ ...prev, area: val ?? '' }))}
                  options={areas.map((area) => ({ value: area, label: area }))}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect
                  label="Utiliza Libro IVA"
                  value={formData.utilizaLibroIva || null}
                  onChange={(val) => setFormData((prev) => ({ ...prev, utilizaLibroIva: val ?? '' }))}
                  options={[
                    { value: 'Si', label: 'Si' },
                    { value: 'No', label: 'No' },
                  ]}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect
                  label="Suma/Resta"
                  value={formData.sumaResta || null}
                  onChange={(val) => setFormData((prev) => ({ ...prev, sumaResta: val ?? '' }))}
                  options={[
                    { value: 'Suma', label: 'Suma' },
                    { value: 'Resta', label: 'Resta' },
                  ]}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect<number>
                  label="Stock Negativo"
                  value={formData.stockNegativo ?? 0}
                  onChange={(val) => setFormData((prev) => ({ ...prev, stockNegativo: val ?? 0 }))}
                  options={[
                    { value: 0, label: 'No (validar stock)' },
                    { value: 1, label: 'Sí (permitir negativo)' },
                  ]}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect<number>
                  label="Descuenta Stock"
                  value={formData.descuentaStock ?? 0}
                  onChange={(val) => setFormData((prev) => ({ ...prev, descuentaStock: val ?? 0 }))}
                  options={[
                    { value: 0, label: 'Sí (descuenta)' },
                    { value: 1, label: 'No (no descuenta)' },
                  ]}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  name="infoCons"
                  label="Info Cons"
                  type="number"
                  value={formData.infoCons ?? ''}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  name="nota" 
                  label="Nota" 
                  value={formData.nota || ''} 
                  onChange={handleChange} 
                  fullWidth 
                  multiline
                  rows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/tipo-formulario')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TipoFormularioForm;
